import { useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Minus, Plus, Trash2 } from "lucide-react";

const VARIANTS = {
  primary: { background: "var(--brand)", color: "var(--on-brand)" },
  dark: { background: "var(--brand-2)", color: "var(--on-brand-2)" },
  outline: { background: "transparent", color: "var(--on-surface)", border: "1px solid var(--border-strong)" },
  ghost: { background: "transparent", color: "var(--brand)" },
};

export const AppButton = ({ title, icon: Icon, variant = "primary", small, loading, disabled, onClick, testId, className = "", type = "button" }) => (
  <button type={type} onClick={onClick} disabled={disabled || loading} data-testid={testId} style={VARIANTS[variant]}
    className={`press rounded-full flex items-center justify-center gap-2 font-medium disabled:opacity-50 ${small ? "min-h-[40px] px-3.5 text-sm" : "min-h-[52px] px-5 text-base"} ${className}`}>
    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : Icon && <Icon className={small ? "h-4 w-4" : "h-5 w-5"} />}
    <span className="truncate">{title}</span>
  </button>
);

export const Segmented = ({ options, value, onChange, testIdPrefix, disabled = [] }) => (
  <div className="flex rounded-full p-1" style={{ background: "var(--surface-3)" }}>
    {options.map((o) => {
      const on = o.key === value;
      const off = disabled.includes(o.key);
      return (
        <button key={o.key} type="button" disabled={off} onClick={() => onChange(o.key)} data-testid={`${testIdPrefix}-${o.key}`}
          className="flex-1 min-h-[40px] rounded-full px-3 text-sm font-medium transition-colors disabled:opacity-35"
          style={on ? { background: "var(--brand-2)", color: "var(--on-brand-2)", boxShadow: "0 1px 3px rgba(0,0,0,.12)" } : { color: "var(--muted-c)" }}>
          {o.label}
        </button>
      );
    })}
  </div>
);

export const Stepper = ({ value, onChange, testIdPrefix, min = 1, trash }) => (
  <div className="flex items-center gap-1 rounded-full p-1" style={{ background: "var(--surface-3)" }}>
    <button type="button" onClick={() => onChange(value - 1)} disabled={!trash && value <= min} data-testid={`${testIdPrefix}-minus`} className="h-9 w-9 rounded-full grid place-items-center disabled:opacity-40" style={{ background: "var(--surface-2)" }}>
      {trash && value <= 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
    </button>
    <span className="w-7 text-center font-medium" data-testid={`${testIdPrefix}-value`}>{value}</span>
    <button type="button" onClick={() => onChange(value + 1)} data-testid={`${testIdPrefix}-plus`} className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "var(--surface-2)" }}><Plus className="h-4 w-4" /></button>
  </div>
);

export const EmptyState = ({ icon: Icon, title, subtitle, children, testId }) => (
  <div className="flex flex-col items-center text-center gap-2 py-12 px-6" data-testid={testId}>
    <div className="h-20 w-20 rounded-full grid place-items-center mb-2" style={{ background: "var(--brand-3)", color: "var(--brand)" }}><Icon className="h-9 w-9" /></div>
    <p className="text-xl font-medium">{title}</p>
    {subtitle && <p className="text-sm mb-3" style={{ color: "var(--muted-c)" }}>{subtitle}</p>}
    {children}
  </div>
);

export const Field = ({ label, testId, multiline, ...props }) => {
  const Tag = multiline ? "textarea" : "input";
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      <Tag data-testid={testId} {...props} className={`w-full rounded-xl px-3.5 text-base outline-none focus:ring-2 ${multiline ? "py-3 min-h-[88px]" : "h-12"}`}
        style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)", color: "var(--on-surface)", "--tw-ring-color": "var(--brand)" }} />
    </label>
  );
};

export const SectionTitle = ({ children }) => <p className="text-base font-medium mb-2">{children}</p>;

export const Card = ({ children, className = "", testId }) => (
  <div className={`rounded-[20px] ${className}`} style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)" }} data-testid={testId}>{children}</div>
);

export const Chip = ({ on, onClick, children, testId, disabled, strike }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testId}
    className="press min-h-[40px] px-3 rounded-full text-[13px] font-medium disabled:opacity-40"
    style={{ border: `1px solid ${on ? "var(--brand)" : "var(--border-c)"}`, background: on ? "var(--brand-3)" : "var(--surface-2)", color: on ? "var(--on-brand-3)" : "var(--on-surface)", textDecoration: strike ? "line-through" : "none" }}>
    {children}
  </button>
);

export const ScreenHeader = ({ title, fallback = "/" }) => {
  const nav = useNavigate();
  const back = () => (window.history.length > 1 ? nav(-1) : nav(fallback));
  return (
    <div className="sticky top-0 z-30 flex items-center px-2 h-[52px]" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border-c)" }}>
      <button type="button" onClick={back} className="w-11 h-11 grid place-items-center" data-testid="screen-back-button"><ChevronLeft className="h-6 w-6" /></button>
      <p className="flex-1 text-center text-base font-medium truncate">{title}</p>
      <span className="w-11" />
    </div>
  );
};

export const Footer = ({ children }) => (
  <div className="sticky bottom-0 z-30 px-4 pt-3 pb-4" style={{ background: "var(--surface)", borderTop: "1px solid var(--border-c)" }}>{children}</div>
);
